import { Sun, Moon, Monitor } from 'lucide-react'
import { useTheme } from '@/shared/hooks/useTheme'
import type { ThemeChoice } from '@/config/runtime-config'

/**
 * The theme control, in the top bar.
 *
 * ## Why it cycles three ways instead of flipping two
 *
 * `config.defaultTheme` is `'system'`, and system is not a starting state the user
 * should be unable to get back to — somebody who follows their OS between day and
 * night loses that the moment they touch a two-state switch, with no way to undo it
 * short of clearing site data. So the button walks light → dark → system.
 *
 * The cost of a three-state cycle is that the next state is not guessable from the
 * icon, which is why the label says it: the icon reports where you ARE, the tooltip
 * and the accessible name report where the click takes you. A screen-reader user
 * gets the same sentence a mouse user gets on hover.
 */

/**
 * The cycle as a total map rather than an array index. `noUncheckedIndexedAccess`
 * is on in this project, so `ORDER[i]` is `ThemeChoice | undefined` and the wrap-
 * around needs a non-null assertion to compile — a lie in exchange for nothing.
 * A Record keyed by ThemeChoice is exhaustive by construction: add a fourth choice
 * to the type and this stops compiling until the cycle is updated too.
 */
const NEXT: Record<ThemeChoice, ThemeChoice> = {
  light: 'dark',
  dark: 'system',
  system: 'light',
}

const FACE: Record<ThemeChoice, { Icon: typeof Sun; now: string }> = {
  light: { Icon: Sun, now: 'Light' },
  dark: { Icon: Moon, now: 'Dark' },
  system: { Icon: Monitor, now: 'Match system' },
}

export function ThemeToggle({ className }: { className?: string }) {
  const { choice, resolved, setTheme } = useTheme()
  const next = NEXT[choice]
  const { Icon, now } = FACE[choice]

  // "Match system" alone does not tell the user what they will actually see, and
  // that is the one choice whose result depends on something outside the page.
  const nextLabel = next === 'system' ? `match system (${resolved})` : next

  return (
    <button
      type="button"
      onClick={() => setTheme(next)}
      className={
        'h-9 w-9 inline-flex items-center justify-center text-muted-foreground ' +
        'hover:bg-muted rounded-md transition-colors ' + (className ?? '')
      }
      title={`Theme: ${now} — switch to ${nextLabel}`}
      aria-label={`Theme: ${now}. Switch to ${nextLabel}.`}
    >
      <Icon className="h-4 w-4" />
    </button>
  )
}
